import { motion } from "framer-motion";
import { useLanguage } from "@/i18n";

// Hero image for Publicidad page (octopus with products)
const PUBLICIDAD_HERO = "https://customer-assets.emergentagent.com/job_5258f97a-514c-45b1-a984-43e415ab135d/artifacts/5be0k5p5_flor.jpeg";

// Helper to convert YouTube URLs to embed format
const getEmbedUrl = (url) => {
  if (url.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes('youtube.com/shorts')) {
    const videoId = url.split('shorts/')[1]?.split('?')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes('youtu.be')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

// Video Player Component
const VideoPlayer = ({ url, className = "" }) => {
  const embedUrl = getEmbedUrl(url);
  return (
    <div className={`relative aspect-video bg-black/50 rounded-sm overflow-hidden shadow-xl ${className}`}>
      <iframe
        src={embedUrl}
        title="Video"
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
};

const PublicidadPage = () => {
  const { t } = useLanguage();
  
  const videos = [
    { url: "https://youtu.be/pNyRsdw6BYg", text: t.publicidad.memoryOfScents },
    { url: "https://youtu.be/4L2yfeQiRzk", text: t.publicidad.cocaCola },
    { url: "https://youtu.be/d_VJGYHg12Y", text: t.publicidad.neurobyte },
    { url: "https://youtu.be/f-C3HClwLW4", text: t.publicidad.whenMonster },
    { url: "https://youtu.be/XYXgPOoHXJQ", text: t.publicidad.paca },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Same style as Ficción con IA */}
      <section className="relative h-[50vh] lg:h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={PUBLICIDAD_HERO}
            alt={t.publicidad.heroTitle}
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 pb-12 lg:pb-16">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide text-foreground"
          >
            {t.publicidad.heroTitle}
          </motion.h1>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-serif text-xl lg:text-2xl font-light text-foreground/80 leading-relaxed italic text-center"
          >
            {t.publicidad.introText}
          </motion.p>
        </div>
      </section>

      {/* Videos Grid - 2x2 layout + 1 */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 space-y-12 lg:space-y-16">
          
          {/* Row 1: 2 videos */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
          >
            {/* Video 1 */}
            <div>
              <VideoPlayer url={videos[0].url} />
              <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed">
                {videos[0].text}
              </p>
            </div>
            {/* Video 2 */}
            <div>
              <VideoPlayer url={videos[1].url} />
              <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed">
                {videos[1].text}
              </p>
            </div>
          </motion.div>

          {/* Row 2: 2 videos */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
          >
            {/* Video 3 */}
            <div>
              <VideoPlayer url={videos[2].url} />
              <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed">
                {videos[2].text}
              </p>
            </div>
            {/* Video 4 */}
            <div>
              <VideoPlayer url={videos[3].url} />
              <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed">
                {videos[3].text}
              </p>
            </div>
          </motion.div>

          {/* Row 3: 1 video centered or 2 videos */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
          >
            {/* Video 5 */}
            <div>
              <VideoPlayer url={videos[4].url} />
              <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed">
                {videos[4].text}
              </p>
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default PublicidadPage;
